import { useId, useState } from 'react';
import { Avatar, Box, Chip, IconButton, Typography } from '@mui/joy';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';

const money = value => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 });

export default function FineCard({ fine, showEmployee = true, selection, onCancel, onDelete }) {
    const [expanded, setExpanded] = useState(false);
    const detailsId = useId();
    const cancelled = fine.is_active === false;
    const kind = fine.is_deduction ? 'Списание' : 'Штраф';
    const accent = cancelled ? 'var(--joy-palette-text-tertiary)' : fine.is_deduction ? '#f59e0b' : '#ef4444';
    const reason = fine.reason?.trim() || 'Причина не указана';
    return <Box sx={{ minWidth: 0, borderBottom: '1px solid var(--joy-palette-divider)', opacity: cancelled ? 0.65 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5 }}>
            {selection}
            <Box component="button" type="button" aria-expanded={expanded} aria-controls={detailsId}
                aria-label={`${kind}${showEmployee ? `: ${fine.photographer_name || 'сотрудник'}` : ''}. Подробнее`}
                onClick={() => setExpanded(value => !value)}
                sx={{ display: 'block', width: '100%', minWidth: 0, p: 1, border: 0, borderRadius: 8,
                    color: 'var(--joy-palette-text-primary)', bgcolor: 'transparent', cursor: 'pointer', textAlign: 'left',
                    '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                    '&:focus-visible': { outline: '2px solid var(--joy-palette-primary-400)', outlineOffset: -2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {showEmployee && <Avatar src={fine.photographer_avatar_url} size="sm" sx={{ flexShrink: 0 }}>
                        {(fine.photographer_name || '?').charAt(0)}
                    </Avatar>}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {showEmployee && <Typography component="span" sx={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-primary-300)', overflowWrap: 'anywhere' }}>
                            {fine.photographer_name}
                        </Typography>}
                        <Box component="span" sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                            <Chip component="span" size="sm" variant="soft" sx={{ fontSize: 10, fontWeight: 700,
                                bgcolor: fine.is_deduction ? 'rgba(245,158,11,0.10)' : 'rgba(239,68,68,0.10)', color: accent }}>{kind}</Chip>
                            {fine.is_administrator && <Chip component="span" size="sm" variant="soft"
                                sx={{ fontSize: 10, bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b' }}>Админ</Chip>}
                            {fine.is_auto && <Chip component="span" size="sm" variant="soft"
                                sx={{ fontSize: 10, bgcolor: 'rgba(245,158,11,0.10)', color: '#f59e0b' }}>Авто</Chip>}
                            {cancelled && <Chip component="span" size="sm" variant="soft"
                                sx={{ fontSize: 10, bgcolor: 'rgba(150,150,150,0.10)', color: 'var(--joy-palette-text-tertiary)' }}>Отменён</Chip>}
                        </Box>
                    </Box>
                    <Typography component="span" sx={{ fontSize: 15, fontWeight: 800, color: accent, flexShrink: 0,
                        fontVariantNumeric: 'tabular-nums', textDecoration: cancelled ? 'line-through' : 'none' }}>
                        −{money(fine.value)} ₽
                    </Typography>
                    <KeyboardArrowDownRoundedIcon sx={{ flexShrink: 0, fontSize: 19,
                        transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
                </Box>
                <Typography component="span" sx={{ display: '-webkit-box', WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
                    fontSize: 12, mt: 0.8, color: 'var(--joy-palette-text-secondary)' }}>{reason}</Typography>
            </Box>
        </Box>
        <Box id={detailsId} hidden={!expanded} sx={{ px: 2.5, pb: expanded ? 1.5 : 0 }}>
            {expanded && <>
                <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Причина</Typography>
                <Typography sx={{ fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{reason}</Typography>
                <Typography sx={{ fontSize: 11, mt: 1, overflowWrap: 'anywhere', color: 'var(--joy-palette-text-secondary)' }}>
                    Точка: {fine.location_name || 'Не указана'}
                </Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                    Выставил: {fine.created_by_name || 'Не указан'}
                </Typography>
                {fine.date_create && <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)' }}>
                    Создано: {new Date(fine.date_create).toLocaleString('ru-RU')}
                </Typography>}
                {(onCancel || onDelete) && <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                    {!cancelled && onCancel && <IconButton size="sm" variant="soft" color="warning"
                        aria-label={`Отменить: ${kind.toLowerCase()}`} onClick={() => onCancel(fine)}><UndoRoundedIcon /></IconButton>}
                    {onDelete && <IconButton size="sm" variant="soft" color="danger"
                        aria-label={`Удалить: ${kind.toLowerCase()}`} onClick={() => onDelete(fine)}><DeleteRoundedIcon /></IconButton>}
                </Box>}
            </>}
        </Box>
    </Box>;
}
